import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Collapse,
  CircularProgress,
  Divider,
  Box,
} from "@mui/material";
import React, { useState, useEffect } from "react";

import { useHandlePermissions } from "0.Mine/1.Hooks/security/permissions";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";

import routes from "../../../routes";

interface AdminSelectModalProps {
  open: boolean;
  onClose: () => void;
  licenseId: string;
}

// Define types for route objects based on your routes.js structure
interface RouteItem {
  type: string;
  name?: string;
  key: string;
  route?: string;
  icon?: React.ReactNode;
  component?: React.ReactNode;
  noCollapse?: boolean;
  collapse?: RouteItem[];
  title?: string;
}

interface PermissionState {
  [key: string]: boolean | { [key: string]: boolean };
}

const AdminSelectModal = ({ open, onClose, licenseId }: AdminSelectModalProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionState>({});
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const { getSinglePermission, editPermissions } = useHandlePermissions();
  const { session } = useBusinessContext();

  useEffect(() => {
    if (open && licenseId) {
      loadPermissions();
    }
  }, [open, licenseId]);

  if (!session) return <>Select a business to get started</>;

  const loadPermissions = async () => {
    setLoading(true);
    try {
      if (!session || !session.selectedBusiness) return;

      const permissionsFromAPI = await getSinglePermission(session.selectedBusiness.id, licenseId);
      const mergedPermissions: PermissionState = {};

      routes.forEach((route) => {
        // Skip the comingSoon category
        if (route.key === "comingSoon") return;

        if (route.type === "collapse" && route.name && route.key) {
          if (permissionsFromAPI[route.key] !== undefined) {
            mergedPermissions[route.key] = permissionsFromAPI[route.key];
          } else {
            mergedPermissions[route.key] = false;
          }

          if (route.collapse) {
            if (typeof permissionsFromAPI[route.key] === "object") {
              mergedPermissions[route.key] = { ...permissionsFromAPI[route.key] };
            } else {
              mergedPermissions[route.key] = {};
            }

            route.collapse.forEach((subRoute) => {
              // Skip comingsoon items
              if (subRoute.key === "comingsoon") return;

              if (typeof mergedPermissions[route.key] === "object") {
                const objectPermissions = mergedPermissions[route.key] as {
                  [key: string]: boolean;
                };
                const apiSubPermissions =
                  typeof permissionsFromAPI[route.key] === "object"
                    ? (permissionsFromAPI[route.key] as { [key: string]: boolean })
                    : {};

                objectPermissions[subRoute.key] =
                  apiSubPermissions[subRoute.key] !== undefined
                    ? apiSubPermissions[subRoute.key]
                    : false;
              }
            });
          }
        }
      });

      setPermissions(mergedPermissions);
      setLoading(false);
    } catch (error) {
      console.error("Error loading permissions:", error);
      setLoading(false);
    }
  };

  const handleToggleCategory = (categoryKey: string) => {
    setExpandedCategories({
      ...expandedCategories,
      [categoryKey]: !expandedCategories[categoryKey],
    });
  };

  const handleTogglePermission = (categoryKey: string, itemKey?: string) => {
    if (itemKey) {
      // Toggle sub-item permission
      if (permissions[categoryKey] && typeof permissions[categoryKey] === "object") {
        const categoryPermissions = permissions[categoryKey] as { [key: string]: boolean };
        setPermissions({
          ...permissions,
          [categoryKey]: {
            ...categoryPermissions,
            [itemKey]: !categoryPermissions[itemKey],
          },
        });
      }
    } else {
      // Toggle whole category
      if (typeof permissions[categoryKey] === "object") {
        const allSelected = Object.values(
          permissions[categoryKey] as { [key: string]: boolean }
        ).every((value) => value);
        const newValue = !allSelected;

        const updatedCategoryPermissions: { [key: string]: boolean } = {};
        Object.keys(permissions[categoryKey] as { [key: string]: boolean }).forEach((key) => {
          updatedCategoryPermissions[key] = newValue;
        });

        setPermissions({
          ...permissions,
          [categoryKey]: updatedCategoryPermissions,
        });
      } else {
        setPermissions({
          ...permissions,
          [categoryKey]: !permissions[categoryKey],
        });
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // This is where API savePermissionsForLicense(licenseId, permissions) should go
      console.log("Saving permissions for license", licenseId, permissions);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSaving(false);
      await editPermissions(session.selectedBusiness.id, licenseId, permissions);
      onClose();
    } catch (error) {
      console.error("Error saving permissions:", error);
      setSaving(false);
    }
  };

  const areAllSubItemsSelected = (categoryKey: string) => {
    if (permissions[categoryKey] && typeof permissions[categoryKey] === "object") {
      return Object.values(permissions[categoryKey] as { [key: string]: boolean }).every(
        (value) => value
      );
    }
    return false;
  };

  const areSomeSubItemsSelected = (categoryKey: string) => {
    if (permissions[categoryKey] && typeof permissions[categoryKey] === "object") {
      return (
        Object.values(permissions[categoryKey] as { [key: string]: boolean }).some(
          (value) => value
        ) &&
        !Object.values(permissions[categoryKey] as { [key: string]: boolean }).every(
          (value) => value
        )
      );
    }
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="permissions-dialog-title"
    >
      <DialogTitle id="permissions-dialog-title">Configura i permessi</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {routes
              .filter(
                (route) =>
                  route.type === "collapse" &&
                  route.name &&
                  !route.type.includes("hidden") &&
                  route.key !== "comingSoon"
              )
              .map((category) => (
                <React.Fragment key={category.key}>
                  {category.collapse ? (
                    <>
                      <ListItem button onClick={() => handleToggleCategory(category.key)}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={areAllSubItemsSelected(category.key)}
                            indeterminate={areSomeSubItemsSelected(category.key)}
                            onChange={() => handleTogglePermission(category.key)}
                            inputProps={{ "aria-labelledby": category.key }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography variant="subtitle1">{category.name}</Typography>}
                        />
                        {expandedCategories[category.key] ? <ExpandLess /> : <ExpandMore />}
                      </ListItem>
                      <Collapse in={expandedCategories[category.key]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {category.collapse
                            .filter((item) => item.key !== "comingsoon")
                            .map((item) => (
                              <ListItem key={item.key} button sx={{ pl: 4 }}>
                                <ListItemIcon>
                                  <Checkbox
                                    edge="start"
                                    checked={
                                      permissions[category.key] &&
                                      typeof permissions[category.key] === "object" &&
                                      (permissions[category.key] as { [key: string]: boolean })[
                                        item.key
                                      ] === true
                                    }
                                    onChange={() => handleTogglePermission(category.key, item.key)}
                                    inputProps={{ "aria-labelledby": item.key }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={<Typography variant="body2">{item.name}</Typography>}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </Collapse>
                    </>
                  ) : (
                    <ListItem button>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={permissions[category.key] === true}
                          onChange={() => handleTogglePermission(category.key)}
                          inputProps={{ "aria-labelledby": category.key }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle1">{category.name}</Typography>}
                      />
                    </ListItem>
                  )}
                  <Divider component="li" />
                </React.Fragment>
              ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || saving}
        >
          {saving ? <CircularProgress size={24} /> : "Save Permissions"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminSelectModal;
